import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import { UsersList } from '../interfaces/UsersList';
import useActivateCollaborator from './functions/useActivateCollaborator';
import useAddCollaborator from './functions/useAddCollaborator';
import useDeleteCollaborator from './functions/useDeleteCollaborator';
import useDownloadCanceledsRelatory from './functions/useDownloadCanceledsRelatory';
import useDownloadUsers from './functions/useDownloadUsers';
import useLoadUsers from './functions/useLoadUsers';
import usePageSearches from './functions/usePageSearches';
import useSuspendCollaborator from './functions/useSuspendCollaborator';
import useUploadFile from './functions/useUploadFile';

type UsersSplitedType = Array<UsersList[]> | [[]];
type FilteredUsersType = UsersList[] | [];

export default function useUsers() {
  const [usersSplited, setUsersSplited] = useState<UsersSplitedType>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [filteredUsers, setFilteredUsers] = useState<FilteredUsersType>([]);

  const statusByCode = {
    0: 'Todos',
    1: 'Ativo',
    2: 'Suspenso',
    3: 'Registrado',
  };

  const {
    fullUsers,
    doesListApiHasError,
    isLoadLoading,
    loadUsers,
    lastUpdate,
    authenticationType,
    companyProductType,
  } = useLoadUsers({
    setUsersSplited,
    setFilteredUsers,
    setCurrentPage,
  });

  const {
    searchTerm,
    selectedCodStatus,
    setSelectedCodStatus,
    handleChangeSearchTerm,
    handleChangeStatusFilter,
    handlePageChange,
    selectedCompany,
    handleCompanyChange,
    statusOptions,
    selectedStatus,
    handleStatusChange,
  } = usePageSearches({
    fullUsers,
    usersSplited,
    currentPage,
    filteredUsers,
    setUsersSplited,
    setFilteredUsers,
    setCurrentPage,
  });

  const {
    isSubmittingFile,
    csvFileName,
    modalImportCsvShow,
    csvAction,
    setCsvAction,
    handleFileUpload,
    handleSubmitFile,
    handleImportShow,
    handleImportModalClose,
  } = useUploadFile({
    loadUsers,
    currentPage,
    authenticationType,
    companyProductType,
  });

  const {
    downloadModalShow,
    setDownloadModalShow,
    selectedFilterDownload,
    setSelectedFilterDownload,
    downloadUsers,
  } = useDownloadUsers();

  const {
    isActivating,
    modalActivateShow,
    setModalActivateShow,
    userBeingActivate,
    setUserBeingActivate,
    activateCollaborator,
  } = useActivateCollaborator({
    loadUsers,
    currentPage,
  });

  const {
    isSuspending,
    modalSuspendShow,
    setModalSuspendShow,
    userBeingSuspended,
    setUserBeingSuspended,
    suspendCollaborator,
  } = useSuspendCollaborator({
    loadUsers,
    currentPage,
  });

  const {
    isDeleting,
    modalDeleteShow,
    setModalDeleteShow,
    userBeingDeleted,
    setUserBeingDeleted,
    deleteCollaborator,
  } = useDeleteCollaborator({
    loadUsers,
    currentPage,
  });

  const {
    isAdding,
    modalCreateShow,
    handleCreateModalShow,
    handleCreateModalClose,
    collaboratorId,
    handleCollaboratorIdChange,
    selectedCompanyForm,
    handleCompanyFormChange,
    addCollaborator,
    productType,
    setProductType,
  } = useAddCollaborator({
    loadUsers,
    currentPage,
    authenticationType,
    companyProductType,
  });

  const { downloadCanceledsRelatory } = useDownloadCanceledsRelatory();

  const pagesQuantity = useMemo(() => usersSplited?.length, [usersSplited.length]);

  function handleTryAgain() {
    loadUsers();
  }

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const isLoading = (
    isLoadLoading
    || isSubmittingFile
    || isActivating
    || isSuspending
    || isDeleting
    || isAdding
  );

  return {
    isLoading,
    fullUsers,
    searchTerm,
    handleChangeSearchTerm,
    setSelectedCodStatus,
    doesListApiHasError,
    filteredUsers,
    setUserBeingSuspended,
    setModalSuspendShow,
    setUserBeingActivate,
    setModalActivateShow,
    setUserBeingDeleted,
    setModalDeleteShow,
    statusByCode,
    selectedCodStatus,
    handleTryAgain,
    modalImportCsvShow,
    csvFileName,
    handleFileUpload,
    handleSubmitFile,
    userBeingActivate,
    activateCollaborator,
    userBeingSuspended,
    suspendCollaborator,
    modalDeleteShow,
    userBeingDeleted,
    deleteCollaborator,
    modalCreateShow,
    collaboratorId,
    handleChangeStatusFilter,
    addCollaborator,
    modalActivateShow,
    modalSuspendShow,
    handleImportShow,
    handleCreateModalShow,
    handleImportModalClose,
    handleCreateModalClose,
    setCsvAction,
    csvAction,
    pagesQuantity,
    downloadUsers,
    downloadModalShow,
    setDownloadModalShow,
    setSelectedFilterDownload,
    selectedFilterDownload,
    currentPage,
    handlePageChange,
    loadUsers,
    handleCollaboratorIdChange,
    selectedCompany,
    handleCompanyChange,
    selectedCompanyForm,
    handleCompanyFormChange,
    lastUpdate,
    authenticationType,
    statusOptions,
    selectedStatus,
    handleStatusChange,
    downloadCanceledsRelatory,
    productType,
    setProductType,
    companyProductType,
  };
}
